// Helpers purs de lecture des résultats A/B (page « Résultats » et journal).
//
// L'API `GET /experiment-results?id=` renvoie une ligne d'analyse PAR JOUR, la
// plus récente en tête, chaque ligne portant les cumuls depuis le démarrage.
// Ce module ne fait que mettre ces lignes en forme : aucune statistique n'est
// recalculée ici, le verdict vient du moteur.
//
// Robustesse : la phase 2 du backend enrichit les lignes (`revenue` objet,
// `segments`) pendant que ce back-office est déjà en ligne. Toute fonction
// tolère donc un champ absent, nul, ou dans sa forme de phase 1 (`revenue`
// nombre) — l'affichage retombe sur « — » plutôt que de casser la page.

export const MIN_WIN_PROBABILITY = 0.999;

// Seuil d'alerte SRM : sous cette p-valeur, la répartition observée s'écarte
// trop des poids déclarés pour que le test soit lisible.
export const SRM_ALERT_P = 0.001;

// isNumber : `Number(null)` vaut 0 et `Number("")` aussi — un champ absent
// s'afficherait donc « 0,00 % » au lieu de « — ». On exige une vraie valeur.
const isNumber = (v) => v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v));

export const fmtPct = (v, digits = 2) =>
    isNumber(v) ? (Number(v) * 100).toFixed(digits).replace(".", ",") + " %" : "—";

export const fmtEur = (v) =>
    isNumber(v) ? Number(v).toLocaleString("fr-FR", { style: "currency", currency: "EUR" }) : "—";

export const fmtNumber = (v) => (isNumber(v) ? Number(v).toLocaleString("fr-FR") : "—");

// fmtEurPrecise : commission par visiteur — quelques centimes, il faut 3
// décimales pour que l'écart entre variantes soit visible.
export const fmtEurPrecise = (v) =>
    isNumber(v)
        ? Number(v).toLocaleString("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 3, maximumFractionDigits: 3 })
        : "—";

const asArray = (v) => (Array.isArray(v) ? v : []);

// sortedRows : lignes triées par date CROISSANTE (l'API les rend décroissantes).
export function sortedRows(rows) {
    return asArray(rows)
        .filter((row) => row && row.date)
        .slice()
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

// latestRow : ligne d'analyse la plus récente, null si aucune analyse.
export function latestRow(rows) {
    const sorted = sortedRows(rows);
    return sorted.length > 0 ? sorted[sorted.length - 1] : null;
}

// variantKeys : clés de variantes rencontrées, dans l'ordre de première
// apparition (A d'abord, donc, puisque l'analyseur écrit le contrôle en tête).
export function variantKeys(rows) {
    const keys = [];
    sortedRows(rows).forEach((row) => {
        asArray(row.variants).forEach((variant) => {
            if (variant && variant.key && keys.indexOf(variant.key) === -1) keys.push(variant.key);
        });
    });
    return keys;
}

// seriesFromRows : séries jour par jour pour la courbe.
//   { labels: [dates croissantes], keys: [clés], exposed: {clé: [n]}, rate: {clé: [taux]} }
// Un jour sans ligne pour une variante donne `null` (trou dans la courbe, pas
// un zéro qui ferait croire à une chute).
export function seriesFromRows(rows) {
    const sorted = sortedRows(rows);
    const keys = variantKeys(sorted);
    const exposed = {};
    const rate = {};
    keys.forEach((key) => {
        exposed[key] = [];
        rate[key] = [];
    });

    sorted.forEach((row) => {
        const byKey = {};
        asArray(row.variants).forEach((variant) => {
            if (variant && variant.key) byKey[variant.key] = variant;
        });
        keys.forEach((key) => {
            const variant = byKey[key];
            exposed[key].push(variant && isNumber(variant.exposed) ? Number(variant.exposed) : null);
            rate[key].push(variant && isNumber(variant.rate) ? Number(variant.rate) : null);
        });
    });

    return { labels: sorted.map((row) => row.date), keys, exposed, rate };
}

// formatVerdict : bandeau de verdict en clair.
//   { label, tone: "info"|"success"|"warning"|"secondary", reason }
// `tone` est un nom de couleur bootstrap : un gagnant proposé ne doit pas avoir
// la même charge visuelle qu'une lecture intermédiaire.
export function formatVerdict(row) {
    if (!row || !row.verdict || !row.verdict.decision) {
        return { label: "Pas encore d'analyse", tone: "secondary", reason: "L'analyseur n'a pas encore produit de ligne pour cette expérience." };
    }
    const { decision, winner, reason } = row.verdict;
    switch (decision) {
        case "stop_winner":
            return {
                label: "Gagnant proposé" + (winner ? " : variante " + winner : ""),
                tone: "success",
                reason: reason || "",
            };
        case "stop_no_diff":
            return { label: "Aucune différence pratique — arrêter", tone: "warning", reason: reason || "" };
        case "stop_max_days":
            return { label: "Durée maximale atteinte, sans conclusion", tone: "warning", reason: reason || "" };
        case "continue":
            return { label: "Lecture intermédiaire — le test continue", tone: "info", reason: reason || "" };
        default:
            return { label: decision, tone: "secondary", reason: reason || "" };
    }
}

// daysRemaining : jours restants avant la durée maximale, null si l'expérience
// n'a pas démarré ou n'a pas de durée maximale. Négatif = durée dépassée.
export function daysRemaining(experiment, now = new Date()) {
    if (!experiment || !experiment.startedAt) return null;
    const maxDays = Number(experiment.rules && experiment.rules.maxDays) || 0;
    if (maxDays <= 0) return null;
    const started = new Date(experiment.startedAt);
    if (isNaN(started.getTime()) || started.getFullYear() < 1971) return null;
    const elapsed = Math.floor((now.getTime() - started.getTime()) / 86400000);
    return maxDays - elapsed;
}

// daysElapsed : jours écoulés depuis le démarrage, null si non démarrée.
export function daysElapsed(experiment, now = new Date()) {
    if (!experiment || !experiment.startedAt) return null;
    const started = new Date(experiment.startedAt);
    if (isNaN(started.getTime()) || started.getFullYear() < 1971) return null;
    return Math.floor((now.getTime() - started.getTime()) / 86400000);
}

// revenueOf : bloc revenu d'une ligne, normalisé.
//   { variantsEUR: {clé: €/visiteur}, probBBeatsA, expectedLoss, expectedLossA }
// Tolère la forme de phase 1 (`revenue` = nombre unique) et l'absence du bloc
// (⇒ null : la page affiche « — », le revenu n'étant jamais décisionnel).
export function revenueOf(row) {
    const revenue = row && row.revenue;
    if (revenue === null || revenue === undefined) return null;
    if (typeof revenue === "number") {
        return Number.isFinite(revenue) ? { variantsEUR: {}, total: revenue } : null;
    }
    if (typeof revenue !== "object") return null;
    const variantsEUR = {};
    const source = revenue.variantsEUR && typeof revenue.variantsEUR === "object" ? revenue.variantsEUR : {};
    Object.keys(source).forEach((key) => {
        if (isNumber(source[key])) variantsEUR[key] = Number(source[key]);
    });
    // Compteurs bruts par variante (acheteurs, ventes, commission totale), ajoutés le
    // 04/10/2026 : {} sur les lignes plus anciennes.
    const pick = (obj) => {
        const out = {};
        const src = obj && typeof obj === "object" ? obj : {};
        Object.keys(src).forEach((key) => { if (isNumber(src[key])) out[key] = Number(src[key]); });
        return out;
    };
    const buyers = pick(revenue.buyers);
    // Taux de conversion sur VENTES RÉELLES (acheteurs identifiés ÷ exposés), calculé
    // par l'analyseur depuis le 09/10/2026. Sur les lignes plus anciennes, on le
    // reconstitue avec les exposés de la ligne (même cohorte à quelques visiteurs
    // près) ; la probabilité, elle, reste « — » faute de tirage côté moteur.
    const conversion = pick(revenue.conversion);
    if (Object.keys(conversion).length === 0) {
        asArray(row.variants).forEach((v) => {
            if (v && buyers[v.key] !== undefined && Number(v.exposed) > 0) {
                conversion[v.key] = buyers[v.key] / Number(v.exposed);
            }
        });
    }
    const lift = revenue.conversionLiftCI90;
    return {
        variantsEUR,
        buyers,
        sales: pick(revenue.sales),
        totalEUR: pick(revenue.totalEUR),
        probBBeatsA: isNumber(revenue.probBBeatsA) ? Number(revenue.probBBeatsA) : null,
        expectedLoss: isNumber(revenue.expectedLoss) ? Number(revenue.expectedLoss) : null,
        expectedLossA: isNumber(revenue.expectedLossA) ? Number(revenue.expectedLossA) : null,
        conversion,
        conversionProbBBeatsA: isNumber(revenue.conversionProbBBeatsA) ? Number(revenue.conversionProbBBeatsA) : null,
        conversionLiftCI90:
            Array.isArray(lift) && lift.length === 2 && isNumber(lift[0]) && isNumber(lift[1])
                ? [Number(lift[0]), Number(lift[1])]
                : null,
    };
}

// segmentsByDimension : ventilation par segment, regroupée par dimension et
// ordonnée par exposition décroissante. [] si le champ est absent.
export function segmentsByDimension(row) {
    const groups = {};
    asArray(row && row.segments).forEach((segment) => {
        if (!segment || !segment.dimension) return;
        const variants = asArray(segment.variants).map((v) => ({
            key: v && v.key,
            exposed: Number(v && v.exposed) || 0,
            converted: Number(v && v.converted) || 0,
        }));
        const total = variants.reduce((sum, v) => sum + v.exposed, 0);
        if (!groups[segment.dimension]) groups[segment.dimension] = [];
        groups[segment.dimension].push({ value: segment.value || "(non renseigné)", variants, totalExposed: total });
    });

    return Object.keys(groups)
        .sort()
        .map((dimension) => ({
            dimension,
            rows: groups[dimension].sort((a, b) => b.totalExposed - a.totalExposed),
        }));
}

export const DIMENSION_LABELS = {
    market: "Marché",
    source: "Source de trafic",
    audience: "Audience (visiteurs du test / ambassadeurs forcés en B, hors comparaison)",
};

export function dimensionLabel(dimension) {
    return DIMENSION_LABELS[dimension] || dimension;
}

// hasSRMAlert : la répartition observée s'écarte trop des poids déclarés — le
// test n'est pas lisible tant que ce n'est pas expliqué.
export function hasSRMAlert(row) {
    if (!row) return false;
    // p == 0 avec chi2 == 0 = « pas encore calculé », pas une alerte.
    if (!isNumber(row.srmP)) return false;
    const p = Number(row.srmP);
    if (p === 0 && !(Number(row.srmChi2) > 0)) return false;
    return p < SRM_ALERT_P;
}

// Commission par visiteur dans la LISTE des expériences (lecture humaine, jamais
// décisionnelle : l'analyseur décide sur les clics). Seuils sur P(B > A) :
// ≥ 95 % « devant », ≥ 80 % « tendance », sinon « non tranché » ; moins de
// COMMISSION_MIN_BUYERS acheteurs dans un bras ⇒ « trop tôt ».
export const COMMISSION_MIN_BUYERS = 30;

export function commissionSummary(row) {
    const revenue = revenueOf(row);
    if (!revenue) return null;
    const keys = asArray(row && row.variants).map((v) => v && v.key).filter((k) => k && revenue.variantsEUR[k] !== undefined);
    if (keys.length === 0) return null;
    const perVariant = keys.map((k) => k + " " + fmtEurPrecise(revenue.variantsEUR[k])).join(" · ");
    const buyersKnown = keys.every((k) => revenue.buyers[k] !== undefined);
    const buyers = buyersKnown ? keys.map((k) => fmtNumber(revenue.buyers[k])).join(" / ") + " acheteurs" : "";
    const tooEarly = buyersKnown && keys.some((k) => revenue.buyers[k] < COMMISSION_MIN_BUYERS);
    const p = revenue.probBBeatsA;
    let reading = "";
    let probText = "";
    if (keys.length === 2 && p !== null) {
        probText = "B > A : " + fmtPct(p, 0);
        if (tooEarly) reading = "trop tôt";
        else if (p >= 0.95) reading = "B devant";
        else if (p <= 0.05) reading = "A devant";
        else if (p >= 0.8) reading = "tendance B";
        else if (p <= 0.2) reading = "tendance A";
        else reading = "non tranché";
    } else if (tooEarly) {
        reading = "trop tôt";
    }
    return { perVariant, probText, reading, buyers, tooEarly };
}
